'use client';

import { useState, useCallback } from 'react';
import dynamic from 'next/dynamic';

// Dynamically import Plot component to avoid SSR issues
const Plot = dynamic(() => import('react-plotly.js'), {
  ssr: false,
  loading: () => <div className="plot-loading">Loading visualization...</div>,
});

export interface PlotlyFallbackProps {
  plotData: any;
  plotLayout: any;
  svgThumbnail?: string | null;
  className?: string;
}

export default function PlotlyFallback({ plotData, plotLayout, svgThumbnail, className }: PlotlyFallbackProps) {
  const [cameraState, setCameraState] = useState<any>(null);

  const handleRelayout = useCallback((eventData: any) => {
    if (eventData?.['scene.camera']) {
      setCameraState(eventData['scene.camera']);
    }
  }, []);

  const getLayoutWithCamera = useCallback(() => {
    const baseLayout = plotLayout || {
      title: { text: '3D Print Path Visualization' },
      scene: {
        xaxis: { title: { text: 'X (mm)' } },
        yaxis: { title: { text: 'Y (mm)' } },
        zaxis: { title: { text: 'Z (mm)' } },
      },
      margin: { l: 0, r: 0, t: 30, b: 0 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      autosize: true,
    };

    if (cameraState && baseLayout.scene) {
      baseLayout.scene.camera = { ...cameraState };
    }

    return baseLayout;
  }, [plotLayout, cameraState]);

  return (
    <div className={className || 'plotly-fallback-wrapper'}>
      {plotData ? (
        <Plot
          data={plotData}
          layout={getLayoutWithCamera()}
          style={{ width: '100%', height: '100%' }}
          config={{ responsive: true }}
          useResizeHandler={true}
          onRelayout={handleRelayout}
        />
      ) : svgThumbnail ? (
        <div className="thumbnail-preview">
          <div className="thumbnail-container">
            <div
              className="preview-svg"
              dangerouslySetInnerHTML={{ __html: svgThumbnail }}
            />
          </div>
          <div className="thumbnail-message">
            Run the code to see the interactive 3D visualization
          </div>
        </div>
      ) : (
        <div className="placeholder">
          Run the code to see the 3D visualization
        </div>
      )}
    </div>
  );
}
